import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

interface Prize {
  rank: number;
  name: string;
  description: string;
  value: number;
  quantity: number;
  claimed: number;
}

interface Campaign {
  _id: string;
  name: string;
  description: string;
  category: string;
  prizes: Prize[];
  startDate: string;
  endDate: string;
  status: string;
  showProgress: boolean;
  showWinners: boolean;
  showStats: boolean;
}

interface CampaignData {
  success: boolean;
  tag: any;
  campaign: Campaign;
  message?: string;
}

const ConsumerLotteryPage: React.FC = () => {
  const { tagId } = useParams<{ tagId: string }>();
  const navigate = useNavigate();
  
  const [campaignData, setCampaignData] = useState<CampaignData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  
  // Prevent duplicate fetches with ref
  const fetchedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  useEffect(() => {
    if (!tagId) {
      setError('No tag ID provided');
      setLoading(false);
      return;
    }
    
    // ⭐ KEY FIX: Prevent duplicate fetches
    if (fetchedRef.current) {
      console.log('Fetch already in progress or completed');
      return;
    }
    
    const fetchCampaignData = async () => {
      // Mark as fetching
      fetchedRef.current = true;
      setLoading(true);
      setError(null);
      
      // Create abort controller for cleanup
      abortControllerRef.current = new AbortController();
      
      try {
        console.log('Fetching campaign data for tagId:', tagId);
        
        const response = await fetch(
          `/api/consumer-lottery/check-tag?tagId=${tagId}`,
          { 
            signal: abortControllerRef.current.signal,
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data: CampaignData = await response.json();
        
        console.log('Campaign data received:', data);
        
        if (data.success) {
          setCampaignData(data);
        } else {
          setError(data.message || 'Failed to load campaign');
          fetchedRef.current = false; // Allow retry on error
        }
      } catch (err: any) {
        // Ignore abort errors (happens on component unmount)
        if (err.name === 'AbortError') {
          console.log('Fetch aborted');
          return;
        }
        
        console.error('Error fetching campaign:', err);
        setError('Failed to load campaign. Please try again.');
        fetchedRef.current = false; // Allow retry on error
      } finally {
        setLoading(false);
      }
    };
    
    fetchCampaignData();
    
    // ⭐ KEY FIX: Cleanup function to abort fetch on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [tagId]); // Only re-run if tagId changes
  
 const handleJoinLottery = async () => {
  if (!campaignData?.campaign?._id) {
    alert('Campaign not loaded');
    return;
  }

  setJoining(true);
  try {
    const token = localStorage.getItem("consumerToken") || "";
    const response = await fetch(`${process.env.REACT_APP_API_URL}/consumer-lottery/join/${tagId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        campaignId: campaignData.campaign._id,
      }),
    });

    const data = await response.json();
    console.log("🎟 Join response:", data);

    if (data.success && data.entry?.entryId) {
      navigate(`/consumer/lottery-spin/${tagId}/${campaignData.campaign._id}`, {
        state: { entryId: data.entry.entryId, result: data, lottery: campaignData.campaign },
      });
    } else {
      alert(data.message || "Failed to join lottery");
    }
  } catch (err) {
    console.error("Error joining lottery:", err);
    alert("Failed to join lottery. Please try again.");
  } finally {
    setJoining(false);
  }
};

  
  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading campaign...</p>
        </div>
      </div>
    );
  }
  
  // Error State
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <button 
              onClick={() => {
                fetchedRef.current = false;
                setLoading(true);
                setError(null);
                // Re-trigger useEffect by forcing a state change
                window.location.reload();
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
            >
              Retry
            </button>
            <button 
              onClick={() => navigate('/consumer')}
              className="w-full border border-gray-300 hover:bg-gray-50 py-3 px-6 rounded-lg font-medium transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // No Data State
  if (!campaignData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">No campaign data available</p>
          <button 
            onClick={() => navigate('/consumer')}
            className="mt-4 bg-blue-600 text-white px-6 py-3 rounded-lg"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }
  
  const { campaign } = campaignData;
  
  // Main Campaign Display
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-8 text-white">
            <h1 className="text-3xl font-bold mb-2">{campaign.name}</h1>
            <p className="text-purple-100">{campaign.description}</p>
          </div>
          
          {/* Campaign Status */}
          <div className="p-6 bg-gray-50 border-b">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm text-gray-600">Category:</span>
                <span className="ml-2 font-semibold text-gray-900">{campaign.category}</span>
              </div>
              <div>
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                  campaign.status === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {campaign.status}
                </span>
              </div>
            </div>
          </div>
          
          {/* Campaign Dates */}
          <div className="p-6 grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Start Date</p>
              <p className="font-medium text-gray-900">
                {new Date(campaign.startDate).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">End Date</p>
              <p className="font-medium text-gray-900">
                {new Date(campaign.endDate).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
        
        {/* Prizes */}
        {campaign.prizes && campaign.prizes.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-3xl mr-3">🎁</span>
              Available Prizes
            </h2>
            <div className="space-y-4">
              {campaign.prizes.map((prize, index) => (
                <div 
                  key={index} 
                  className="border-2 border-gray-200 rounded-xl p-5 hover:border-purple-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-bold">
                          Rank {prize.rank}
                        </span>
                        <h3 className="text-xl font-bold text-gray-900">{prize.name}</h3>
                      </div>
                      <p className="text-gray-600 mb-3">{prize.description}</p>
                      <div className="flex items-center gap-6 text-sm">
                        <div>
                          <span className="text-gray-600">Value:</span>
                          <span className="ml-2 font-bold text-green-600">${prize.value}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Available:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {prize.quantity - prize.claimed} / {prize.quantity}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  {campaign.showProgress && (
                    <div className="mt-4">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full transition-all"
                          style={{ 
                            width: `${((prize.quantity - prize.claimed) / prize.quantity) * 100}%` 
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Join Button */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <button
            onClick={handleJoinLottery}
            disabled={joining || campaign.status !== 'active'}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
              joining || campaign.status !== 'active'
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl'
            }`}
          >
            {joining ? (
              <span className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                Joining...
              </span>
            ) : campaign.status !== 'active' ? (
              'Campaign Inactive'
            ) : (
              '🎉 Join Lottery Now!'
            )}
          </button>
          
          <button
            onClick={() => navigate('/consumer')}
            className="w-full mt-3 py-3 border-2 border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Back to Scan
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsumerLotteryPage;